import { createFeature, createReducer, on } from '@ngrx/store'
import { ViewerState } from './viewer.state'
import { viewerActions } from './viewer.actions'
import { caseActions } from '../case/case.actions'

const createInitialState = (): ViewerState => ({
    dimensions: undefined,
    crossSectionScale: undefined,
    layout: undefined,
    position: undefined,
    layers: [],
    layerGroups: [],
    expandedNodes: [],
    originalLayers: [],
    searchKey: '',
})

export const viewerFeature = createFeature({
    name: 'viewer',
    reducer: createReducer<ViewerState>(
        createInitialState(),
        on(viewerActions.configLoaded, (state, { config, queryParams }): ViewerState => {
            return config
                ? {
                      ...state,
                      ...config,
                      position: queryParams.position ?? config.position,
                      crossSectionScale: queryParams.zoom ?? config.crossSectionScale,
                      layers:
                          config?.layers?.map(configLayer => {
                              const paramLayer = queryParams.layers?.find(
                                  paramLayer => paramLayer.id === configLayer.id
                              )
                              return paramLayer ? { ...configLayer, ...paramLayer } : configLayer
                          }) ?? [],
                  }
                : state
        }),
        on(viewerActions.setZoom, (state, { zoom }): ViewerState => {
            if (zoom === 0) {
                console.error('bug: attempt to set zoom to zero (setZoom)')
                zoom = 1
            }
            return {
                ...state,
                crossSectionScale: zoom,
            }
        }),
        on(viewerActions.setPosition, (state, { position }): ViewerState => {
            return {
                ...state,
                position,
            }
        }),
        on(viewerActions.setExpandedNodes, (state, { expandedNodes }): ViewerState => {
            return {
                ...state,
                expandedNodes,
            }
        }),
        on(viewerActions.setLayout, (state, { layout }): ViewerState => {
            return {
                ...state,
                layout,
            }
        }),
        on(
            viewerActions.setLayerVisibility,
            (state, { name, visible }): ViewerState => ({
                ...state,
                layers: state.layers?.map(l =>
                    l.name !== name
                        ? l
                        : {
                              ...l,
                              visible,
                          }
                ),
            })
        ),
        on(
            viewerActions.setLayerOpacity,
            (state, { name, opacity }): ViewerState => ({
                ...state,
                layers: state.layers?.map(l =>
                    l.name !== name
                        ? l
                        : {
                              ...l,
                              opacity,
                          }
                ),
            })
        ),
        on(
            viewerActions.setLayerOpacityUnselected,
            (state, { name, opacity }): ViewerState => ({
                ...state,
                layers: state.layers?.map(l =>
                    l.name !== name
                        ? l
                        : {
                              ...l,
                              opacityUnselected: opacity,
                          }
                ),
            })
        ),
        on(
            viewerActions.setLayerSaturation,
            (state, { name, saturation }): ViewerState => ({
                ...state,
                layers: state.layers?.map(l =>
                    l.name !== name
                        ? l
                        : {
                              ...l,
                              saturation,
                          }
                ),
            })
        ),
        on(viewerActions.setLayerShaderColor, (state, { name, shaderColor }): ViewerState => {
            const l = state.layers?.find(l => l.name === name)
            return !!l && l.shaderColor !== shaderColor
                ? {
                      ...state,
                      layers: state.layers?.map(l =>
                          l.name !== name
                              ? l
                              : {
                                    ...l,
                                    shaderColor,
                                }
                      ),
                  }
                : state
        }),
        on(
            viewerActions.setLayerBrightness,
            (state, { name, brightness }): ViewerState => ({
                ...state,
                layers: state.layers?.map(l =>
                    l.name !== name
                        ? l
                        : {
                              ...l,
                              brightness,
                          }
                ),
            })
        ),
        on(
            viewerActions.setLayerContrast,
            (state, { name, contrast }): ViewerState => ({
                ...state,
                layers: state.layers?.map(l =>
                    l.name !== name
                        ? l
                        : {
                              ...l,
                              contrast,
                          }
                ),
            })
        ),
        on(
            viewerActions.setLayerSegmentationColor,
            (state, { name, segmentationColor }): ViewerState => ({
                ...state,
                layers: state.layers?.map(l =>
                    l.name !== name
                        ? l
                        : {
                              ...l,
                              segmentationColor,
                          }
                ),
            })
        ),
        on(viewerActions.setSearchKey, (state, { searchKey }): ViewerState => {
            return {
                ...state,
                searchKey,
            }
        }),
        on(caseActions.loadCase, (): ViewerState => createInitialState())
    ),
})

export const { reducer } = viewerFeature
